import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { verifyEmail } from '../features/auth';
import { PaddiqLogo } from '@kangaroo/ui';

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? 'Request failed';
  }
  return error instanceof Error ? error.message : 'Unknown error';
}

export function VerifyEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') ?? '', [location.search]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(token ? 'loading' : 'error');
  const [message, setMessage] = useState(token ? '' : 'Verification link is missing.');

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;
    void verifyEmail(token)
      .then(() => {
        if (!cancelled) {
          setStatus('success');
          setMessage('Your email is verified. You can sign in now.');
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setStatus('error');
          setMessage(getErrorMessage(error));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Paper sx={{ width: '100%', maxWidth: 420, p: 3 }}>
        <Stack spacing={2.5} alignItems="center">
          <PaddiqLogo />
          <Typography variant="h5" fontWeight={800}>Verify email</Typography>
          {status === 'loading' ? <Typography color="text.secondary">Confirming your email…</Typography> : null}
          {status !== 'loading' ? <Alert severity={status === 'success' ? 'success' : 'error'}>{message}</Alert> : null}
          <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>Go to sign in</Button>
          <Button component={RouterLink} to="/login" size="small">Back to sign in</Button>
        </Stack>
      </Paper>
    </Box>
  );
}
